import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Checkbox,
    Typography, IconButton, Input,
    Textarea
} from "@material-tailwind/react";
import { useEffect, useState } from "react";
import Select from "react-select";
import { useSelector, useDispatch } from "react-redux";
import { XMarkIcon, PlusIcon, TrashIcon, MinusIcon } from "@heroicons/react/24/outline";
import Toaster from "@/components/Toaster";
import { fetchColorAttribute, fetchSizeAttribute } from "@/store/slices/productSlice";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ShowLoader from "@/components/Loader";
import ScrollToTop from "@/components/ScrollToTop";
import * as yup from "yup";
import { resetStatus, addCustomRfq, fetchCustomer, fetchCartItem } from "@/store/slices/rfqSlice";
import ProductVariantSelector from "../../products/product-varients";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import FilePreview from "@/components/FilePreview";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import DeliveryLocationSearch from "@/components/DeliveryLocationSearch";
import { useTranslation } from "react-i18next";

const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const notBlankRegex = /\S/;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;

const schema = yup.object().shape({
    rfq_reference: yup
        .string()
        .required("rfq_ref_num_required")
        .matches(notBlankRegex, "field_not_empty")
        .matches(noHTMLRegex, "html_not_allowed")
        .matches(noCodeRegex, "code_not_allowed")
        .matches(noConsecutiveSpecialChars, "no_consecutive_special_chars")
        .matches(noLeadingTrailingPunct, "no_leading_trailing_punct")
        .min(3, "rfq_ref_min_chars")
        .max(15, "rfq_ref_max_chars"),

    user_id: yup
        .string()
        .required("customer_required"),

    customer_delivery_address: yup.string().nullable().notRequired(),
    latitude: yup.number().nullable().notRequired(),
    longitude: yup.number().nullable().notRequired(),

    products: yup
        .array()
        .of(
            yup.object().shape({
                product_id: yup
                    .number()
                    .typeError("product_required")
                    .required("product_required"),

                quantity: yup
                    .number()
                    .required("quantity_required")
                    .min(1, "quantity_min"),

                attribute_value_color_id: yup
                    .number()
                    .typeError("color_required")
                    .required("color_required"),

                attribute_value_size_id: yup
                    .number()
                    .typeError("size_required")
                    .required("size_required"),
            })
        )
        .min(1, "at_least_one_product"),
    currency_code: yup
        .string()
        .trim()
        .uppercase()
        .length(3, "currency_code_length")
        .required("currency_code_required"),

    comment: yup
        .string()
        .required("comment_required")
        .min(10, "comment_min_chars")
        .max(255, "comment_max_chars")
        .matches(noConsecutiveSpecialChars, "no_consecutive_special_chars")
        .matches(noLeadingTrailingPunct, "no_leading_trailing_punct")
        .test(
            "not-only-whitespace",
            "comment_no_spaces",
            (value) => !value || notBlankRegex.test(value)
        )
        .test(
            "no-html",
            "comment_no_html",
            (value) => !value || noHTMLRegex.test(value)
        )
        .test(
            "no-code",
            "comment_no_code",
            (value) => !value || noCodeRegex.test(value)
        ),
    custom_rfq_file: yup
        .array()
        .nullable()
        .required("files_required")
        .test(
            "has-files",
            "files_required_alt",
            (value) => Array.isArray(value) && value.length > 0
        )
        .test(
            "fileType",
            "file_type_image_pdf",
            (value) => {
                if (!value || value.length === 0) return true;

                return value.every((file) => {
                    return (
                        (file?.type && file.type.startsWith("image/")) ||
                        file?.type === "application/pdf"
                    );
                });
            }
        ),
});

const generateRFQRef = () =>
    `RFQ-${Date.now().toString().slice(-8)}`;

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: '8px',
        minHeight: '44px',
        borderColor: state.isFocused ? '#212121' : '#b0bec5',
        boxShadow: state.isFocused ? '0 0 0 1px #212121' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#212121' : '#b0bec5',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#9e9e9e',
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 50,
        borderRadius: '8px',
    })
};

export function CustomRFQCreate({ onClose }) {

    const dispatch = useDispatch();
    const [isError, setIsError] = useState("");
    const { t } = useTranslation('rfq');
    const { t: ct } = useTranslation("common");
    
    const navigate = useNavigate();

    const { customers, loading } = useSelector((state) => state.rfq);

    const { currencies } = useSelector((state) => state.currentCurrency);

    const {
        control,
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
        reset
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            rfq_reference: "",
            user_id: null,
            material_submittal: null,
            currency_code: null,
            customer_delivery_address: '',
            latitude: null,
            longitude: null,
            custom_rfq_file: [],
            comment: ''
        }
    });

    useEffect(() => {
        dispatch(fetchCustomer());
    }, [dispatch]);

    const onSubmit = async (data) => {
        try {
            const result = await dispatch(addCustomRfq(data)).unwrap();
            if (result.status) {
                Toaster.success(ct('rfqCreateSuccess'));
                dispatch(resetStatus());
                reset();
                onClose();
              
                navigate("/dashboard/rfqs");
            } else {
                setIsError(result.message || ct("somethingWentWrong"));
                setTimeout(() => {
                    setIsError('');
                }, 8000);
                dispatch(resetStatus());
            }
        } catch (err) {
            setIsError(err.message || ct("somethingWentWrong"));
            setTimeout(() => {
                setIsError('');
            }, 8000);
            dispatch(resetStatus());
        }
    };

    const custom_rfq_file = watch("custom_rfq_file");

    const ALLOWED_MIME_TYPES = ["application/pdf"];

    const isValidFileType = (file) =>
        file.type.startsWith("image/") ||
        ALLOWED_MIME_TYPES.includes(file.type);

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);
        const existing = watch("custom_rfq_file") || [];

        const validFiles = newFiles.filter(isValidFileType);

        setValue("custom_rfq_file", [...existing, ...validFiles], {
            shouldValidate: true,
        });
    };

    const removeImage = (index) => {
        const updated = [...custom_rfq_file];
        updated.splice(index, 1);
        setValue("custom_rfq_file", updated, { shouldValidate: true });
    }

    const currencyOptions =
    currencies?.map((currency) => ({
        value: currency.currency_code,
        label: `${currency.currency_code} - ${currency.currency_name}`,
    })) || [];

    return (
        <Dialog
            open={true}
            handler={onClose}
            size="lg"
            className="rounded-2xl p-0"
        >
            {loading && <ShowLoader />}
            <ScrollToTop />

            {/* HEADER */}
            <DialogHeader className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-2xl font-bold text-gray-900">{t('create_new_custom_rfq_title')}</h2>
            </DialogHeader>

            <form id="rfq-create-form" onSubmit={handleSubmit(onSubmit)}>

                <DialogBody className="px-6 max-h-[65vh] overflow-y-auto">
                    {isError &&
                        <div className="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400" role="alert">
                            <span className="font-medium">{ct('oops')}</span> {isError}
                        </div>
                    }

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <Typography variant="small" color="blue-gray" className="mb-2 font-medium capitalize">
                                {t('rfq_reference_input_label')} <span className="text-red-500">*</span>
                            </Typography>
                            <Input
                                type="text"
                                placeholder={t('rfq_reference_placeholder')}
                                {...register('rfq_reference')}
                                className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                                labelProps={{
                                    className: "before:content-none after:content-none",
                                }}
                            />
                            {errors.rfq_reference && (
                                <p className="text-red-600 text-sm mt-1">
                                    {t(errors.rfq_reference.message)}
                                </p>
                            )}
                        </div>

                        <div>
                            <Typography variant="small" color="blue-gray" className="mb-2 font-medium capitalize">
                                {t('customer_label')} <span className="text-red-500">*</span>
                            </Typography>
                            <Select
                                placeholder={t('select_customer_placeholder')}
                                options={customers}
                                onChange={(c) => setValue("user_id", c?.value, { shouldValidate: true })}
                                styles={customSelectStyles}
                                isClearable
                                noOptionsMessage={() => t('no_customer_available')}
                                value={customers.find(opt => opt.value === watch('user_id')) || null}
                            />
                            {errors.user_id && (
                                <p className="text-red-600 text-sm mt-1">
                                    {t(errors.user_id.message)}
                                </p>
                            )}
                        </div>

                        <div className="md:col-span-2">
                            <Typography variant="small" color="blue-gray" className="mb-2 font-medium capitalize">
                                {t('customer_address_optional_label')}
                            </Typography>
                            <Controller
                                name="customer_delivery_address"
                                control={control}
                                render={({ field }) => (
                                    <DeliveryLocationSearch
                                        value={field.value}
                                        latitude={watch("latitude")}
                                        longitude={watch("longitude")}
                                        placeholder={t('searchAddressPlaceholder')}
                                        onChange={(geoData) => {
                                            setValue("customer_delivery_address", geoData.delivery_address || "", { shouldValidate: true });
                                            setValue("latitude", geoData.latitude, { shouldValidate: true });
                                            setValue("longitude", geoData.longitude, { shouldValidate: true });
                                        }}
                                        error={errors.customer_delivery_address?.message}
                                    />
                                )}
                            />
                        </div>

                        {/* Currency Code */}
                         <div className="md:col-span-2">
                            <Typography variant="small" className="font-medium">
                                {t("currencyCodeLabel")} <span className="text-red-500">*</span>
                            </Typography>
                            <Select
                                placeholder={t("currencyCodePlaceholder")}
                                options={currencyOptions}
                                onChange={(selected) => {
                                    setValue("currency_code", selected?.value || "", {
                                    shouldValidate: true,
                                    });
                                }}
                                value={
                                    currencyOptions.find(
                                    (option) => option.value === watch("currency_code")
                                    ) || null
                                }
                                isClearable
                                />
                            <p className="text-red-500 text-sm mt-1">
                                {t(errors.currency_code?.message)}
                            </p>
                        </div>

                        <Controller
                            name="material_submittal"
                            control={control}
                            defaultValue="no"
                            render={({ field }) => (
                                <Checkbox
                                    checked={field.value === "yes"}
                                    onChange={(e) => field.onChange(e.target.checked ? "yes" : "no")}
                                    label={
                                        <Typography
                                            variant="small"
                                            color="gray"
                                            className="flex items-center justify-start font-medium"
                                        >
                                            {t('provide_material_submittal_label')}
                                        </Typography>
                                    }
                                    containerProps={{ className: "-ml-2.5" }}
                                />
                            )}
                        />
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        <div className="flex flex-col gap-1">
                            <Typography variant="small" color="blue-gray" className="mb-2 font-medium capitalize">
                                {t('upload_custom_file_label')} <span className="text-red-500">*</span>
                            </Typography>
                            <label
                                htmlFor="categoryImage"
                                className="h-11 w-58 h-40 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border-2 border-dashed border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                            >
                                <input
                                    id="categoryImage"
                                    type="file"
                                    className="hidden"
                                    multiple
                                    accept="image/*,application/pdf"
                                    {...register("custom_rfq_file")}
                                    onChange={handleFileChange}
                                />
                                <div className="flex flex-col items-center justify-center cursor-pointer">
                                    <PlusCircleIcon className="h-6 w-6 mb-1" />
                                    <p className="text-sm font-medium">{t('upload_custom_file_label')}</p>
                                </div>
                            </label>
                            <p className="text-red-500 text-sm mt-1">{t(errors.custom_rfq_file?.message)}</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        {custom_rfq_file?.map((file, index) => (
                            <FilePreview
                                key={index}
                                file={file}
                                onRemove={() => removeImage(index)}
                                isView={false}
                                containerClass="relative w-58 h-40 mt-2 rounded-md overflow-hidden border border-gray-300"
                            />
                        ))}
                    </div>

                    <div className="mt-4">
                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium mt-2 capitalize">
                            {t('add_your_comments_label')}
                        </Typography>
                        <Textarea
                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                            labelProps={{
                                className: "before:content-none after:content-none",
                            }}
                            placeholder={t('enter_your_comments_placeholder')}
                            type="text"
                            {...register("comment")}
                        />
                        {errors.comment && <p className="text-red-500 text-sm">{t(errors.comment?.message)}</p>}
                    </div>

                </DialogBody>

                {/* FOOTER */}
                <DialogFooter className="px-6 border-t mt-4 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2.5 rounded-lg text-gray-700 border border-gray-300 hover:bg-gray-50 transition"
                    >
                        {ct('cancel')}
                    </button>
                    <button
                        type="submit"
                        form="rfq-create-form"
                        className="px-6 py-2.5 rounded-lg bg-yellow-600 hover:bg-yellow-700 text-black font-semibold transition"
                    >
                        {t('create_custom_rfq_button_modal')}
                    </button>
                </DialogFooter>
            </form>
        </Dialog>
    );
}

export default CustomRFQCreate;